import { defineStore } from 'pinia'
import { Station } from '../types/stationTypes'
import { Passenger } from '../types/passengerTypes'
import { fetchDestinationSuggestions } from '../services/journeyService'
import { findStation, resolveSearchStation } from '../utils/stationAgglomeration'

interface TravelSearchState {
  origin: string
  destination: string
  OriginStation: Station
  destinationStation: Station
  destinationSuggestions: string[]
  departureDate: string
  returnDate: string
  allStations: Station[]
  passengers: Passenger[]
}

const createEmptyStation = (): Station => ({
  name: '',
  _u_i_c_station_code: '',
})

export const useTravelSearchStore = defineStore('travelSearch', {
  state: (): TravelSearchState => ({
    origin: '',
    destination: '',
    OriginStation: createEmptyStation(),
    destinationStation: createEmptyStation(),
    destinationSuggestions: [],
    departureDate: new Date().toISOString().split('T')[0],
    returnDate: '',
    allStations: [],
    passengers: [],
  }),

  actions: {
    setPassengers(passengers: Passenger[]) {
      this.passengers = passengers
    },

    setOriginDestination(origin: string, destination: string) {
      this.origin = origin
      this.destination = destination
    },

    reset() {
      this.origin = ''
      this.destination = ''
    },

    // Searches must use a member stop's agglomeration (see resolveSearchStation).
    async setOriginStation(station: Station) {
      const searchStation = resolveSearchStation(station, this.allStations)
      this.OriginStation = searchStation
      this.origin = searchStation.name || ''
      this.destinationSuggestions = []

      try {
        const code = searchStation._u_i_c_station_code
        const suggestions = await fetchDestinationSuggestions(code)
        // A later pick may have replaced the origin while this request was in flight.
        if (this.OriginStation._u_i_c_station_code === code) {
          this.destinationSuggestions = suggestions
        }
      } catch (error) {
        console.error('Error fetching destination suggestions:', error)
      }
    },

    setDestinationStation(station: Station) {
      this.destinationStation = resolveSearchStation(station, this.allStations)
      this.destination = this.destinationStation.name || ''
    },

    setDepartureDate(date: string) {
      this.departureDate = date
    },

    setReturnDate(date: string | null) {
      this.returnDate = date ?? ''
    },

    setAllStations(stations: Station[]) {
      this.allStations = stations
      // A station picked before the list loaded (e.g. from search history) may be a member stop.
      if (resolveSearchStation(this.OriginStation, stations) !== this.OriginStation) {
        this.setOriginStation(this.OriginStation)
      }
      if (resolveSearchStation(this.destinationStation, stations) !== this.destinationStation) {
        this.setDestinationStation(this.destinationStation)
      }
    },
  },

  getters: {
    // A code missing from the station list still shows, under its code.
    destinationSuggestionStations: (state): Station[] =>
      state.destinationSuggestions.map(
        (code) => findStation(code, state.allStations) ?? { name: code, _u_i_c_station_code: code },
      ),
  },
})
